import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Camera, X } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useRef } from "react";
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

interface WorkingHour {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

interface ServiceItem {
  id: string;
  name: string;
  assigned: boolean;
}

const EmployeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useLanguage();
  const isEditing = !!id;

  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(true);
  const [useCustomHours, setUseCustomHours] = useState(false);
  const [workingHours, setWorkingHours] = useState<WorkingHour[]>(
    Array.from({ length: 7 }, (_, i) => ({
      day_of_week: i + 1,
      is_open: i < 5,
      open_time: "09:00",
      close_time: "17:00",
    }))
  );
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);

  useEffect(() => {
    if (!profile?.organisation_id) return;

    // Fetch services
    supabase
      .from("services")
      .select("id, name")
      .eq("organisation_id", profile.organisation_id)
      .order("sort_order")
      .then(({ data }) => {
        const svcList = (data || []).map((s: any) => ({ id: s.id, name: s.name, assigned: false }));

        if (isEditing) {
          // Fetch employee data + working hours + assigned services
          Promise.all([
            supabase.from("employees").select("*").eq("id", id).single(),
            supabase.from("employee_working_hours").select("*").eq("employee_id", id),
            supabase.from("employee_services").select("service_id").eq("employee_id", id),
          ]).then(([empRes, hoursRes, svcRes]) => {
            if (empRes.data) {
              const emp = empRes.data as any;
              setName(emp.name);
              setImageUrl(emp.image_url || "");
              setActive(emp.active);
            }
            if (hoursRes.data && hoursRes.data.length > 0) {
              setUseCustomHours(true);
              const hrs = (hoursRes.data as any[]).map((h) => ({
                day_of_week: h.day_of_week,
                is_open: h.is_open,
                open_time: h.open_time?.slice(0, 5) || "09:00",
                close_time: h.close_time?.slice(0, 5) || "17:00",
              }));
              // Merge with defaults for missing days
              setWorkingHours(
                Array.from({ length: 7 }, (_, i) => {
                  const existing = hrs.find((h) => h.day_of_week === i + 1);
                  return existing || { day_of_week: i + 1, is_open: false, open_time: "09:00", close_time: "17:00" };
                })
              );
            }
            const assignedIds = new Set((svcRes.data || []).map((s: any) => s.service_id));
            setServices(svcList.map((s) => ({ ...s, assigned: assignedIds.has(s.id) })));
            setFetching(false);
          });
        } else {
          setServices(svcList);
        }
      });
  }, [profile?.organisation_id, id, isEditing]);

  const updateHour = (dayIndex: number, field: keyof WorkingHour, value: any) => {
    setWorkingHours((prev) =>
      prev.map((h, i) => (i === dayIndex ? { ...h, [field]: value } : h))
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadImage = async (employeeId: string): Promise<string | null> => {
    if (!imageFile) return imageUrl || null;
    setUploading(true);
    const ext = imageFile.name.split(".").pop() || "jpg";
    const path = `${profile!.organisation_id}/${employeeId}.${ext}`;
    const { error } = await supabase.storage.from("employee-images").upload(path, imageFile, { upsert: true });
    setUploading(false);
    if (error) throw error;
    const { data } = supabase.storage.from("employee-images").getPublicUrl(path);
    return data.publicUrl + `?t=${Date.now()}`;
  };

  const handleSave = async () => {
    if (!name.trim() || !profile?.organisation_id) return;
    setLoading(true);
    try {
      let employeeId = id;

      if (isEditing) {
        const uploadedUrl = await uploadImage(id!);
        const { error } = await supabase
          .from("employees")
          .update({ name: name.trim(), image_url: uploadedUrl, active })
          .eq("id", id!);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("employees")
          .insert({ organisation_id: profile.organisation_id, name: name.trim(), image_url: null, active })
          .select("id")
          .single();
        if (error) throw error;
        employeeId = (data as any).id;
        const uploadedUrl = await uploadImage(employeeId!);
        if (uploadedUrl) {
          await supabase.from("employees").update({ image_url: uploadedUrl }).eq("id", employeeId!);
        }
      }

      // Save working hours
      if (useCustomHours) {
        await supabase.from("employee_working_hours").delete().eq("employee_id", employeeId!);
        const rows = workingHours.map((h) => ({
          employee_id: employeeId!,
          day_of_week: h.day_of_week,
          is_open: h.is_open,
          open_time: h.open_time,
          close_time: h.close_time,
        }));
        await supabase.from("employee_working_hours").insert(rows);
      } else if (isEditing) {
        // Remove custom hours if toggled off
        await supabase.from("employee_working_hours").delete().eq("employee_id", employeeId!);
      }

      // Save service assignments
      await supabase.from("employee_services").delete().eq("employee_id", employeeId!);
      const assignedServices = services.filter((s) => s.assigned);
      if (assignedServices.length > 0) {
        await supabase.from("employee_services").insert(
          assignedServices.map((s) => ({ employee_id: employeeId!, service_id: s.id }))
        );
      }

      toast.success(isEditing ? t("employees.updated") : t("employees.created"));
      navigate("/dashboard/employees");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 text-muted-foreground">
        <Link to="/dashboard/employees">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("employees.back")}
        </Link>
      </Button>

      <h1 className="text-xl font-bold text-foreground mb-6">
        {isEditing ? t("employees.edit_title") : t("employees.add_title")}
      </h1>

      {/* Basic Info */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <h2 className="text-base font-semibold text-foreground mb-4">{t("employees.basic_info")}</h2>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="relative group">
              <Avatar className="h-20 w-20">
                <AvatarImage src={imagePreview || imageUrl || undefined} />
                <AvatarFallback className="text-xl">{name ? name.slice(0, 2).toUpperCase() : "?"}</AvatarFallback>
              </Avatar>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center rounded-full bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Camera className="h-5 w-5 text-background" />
              </button>
              {(imagePreview || imageUrl) && (
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-xs"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label>{t("employees.name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("employees.name_placeholder")} />
              <p className="text-xs text-muted-foreground">{t("employees.photo_hint")}</p>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-muted/50 p-4">
            <div>
              <p className="text-sm font-medium text-foreground">{t("employees.status_label")}</p>
              <p className="text-xs text-muted-foreground">{active ? t("employees.status_active_desc") : t("employees.status_inactive_desc")}</p>
            </div>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>
        </div>
      </section>

      {/* Service Assignments */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <h2 className="text-base font-semibold text-foreground mb-4">{t("employees.services")}</h2>
        {services.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("employees.no_services")}</p>
        ) : (
          <div className="space-y-3">
            {services.map((svc, idx) => (
              <label key={svc.id} className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={svc.assigned}
                  onCheckedChange={(checked) =>
                    setServices((prev) =>
                      prev.map((s, i) => (i === idx ? { ...s, assigned: !!checked } : s))
                    )
                  }
                />
                <span className="text-sm text-foreground">{svc.name}</span>
              </label>
            ))}
          </div>
        )}
      </section>

      {/* Working Hours */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">{t("employees.working_hours")}</h2>
            <p className="text-xs text-muted-foreground">{t("employees.working_hours_desc")}</p>
          </div>
          <Switch checked={useCustomHours} onCheckedChange={setUseCustomHours} />
        </div>

        {useCustomHours && (
          <div className="space-y-3">
            {workingHours.map((h, idx) => (
              <div key={h.day_of_week} className="flex items-center gap-3">
                <div className="w-10">
                  <Switch checked={h.is_open} onCheckedChange={(v) => updateHour(idx, "is_open", v)} />
                </div>
                <span className="w-12 text-sm font-medium text-foreground">{t(`days.${DAY_KEYS[idx]}`)}</span>
                {h.is_open ? (
                  <div className="flex items-center gap-2">
                    <Input type="time" className="w-28" value={h.open_time} onChange={(e) => updateHour(idx, "open_time", e.target.value)} />
                    <span className="text-muted-foreground">–</span>
                    <Input type="time" className="w-28" value={h.close_time} onChange={(e) => updateHour(idx, "close_time", e.target.value)} />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">{t("employees.closed")}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={!name.trim() || loading}>
          {loading ? t("employees.saving") : isEditing ? t("employees.save_changes") : t("employees.create")}
        </Button>
        <Button variant="ghost" asChild>
          <Link to="/dashboard/employees">{t("employees.cancel")}</Link>
        </Button>
      </div>
    </div>
  );
};

export default EmployeeForm;
